import { dbService } from '../firebaseInstance';
import { errorLogger } from '../utils/errorLogger';
import {
  updateThreadsToFirestore,
  getExistingThreadsFromFirestore,
  filterUniqueThreads
} from '../utils/threadDateRangeUpdate';
import { initializeShardingMetadata } from '../utils/firestoreShardingUtils';
import { graphGet } from '../utils/graphProxy';

const MIN_SINCE = 1688540400; // 2023-07-05 07:00:00Z

const isoToUnixClamped = (iso, clampMin = true) => {
  const ts = Math.floor(new Date(iso).getTime() / 1000);
  if (!clampMin) return ts;
  return Math.max(ts, MIN_SINCE);
};

const nowUnix = () => Math.floor(Date.now() / 1000);

const shouldKeepThread = (thread) => {
  const hasText = !!thread.text;
  const hasMedia = !!thread.media_url;
  const hasChildrenMedia = !!(thread.children && thread.children.data && Array.isArray(thread.children.data) && thread.children.data.some((c) => c.media_url));
  return hasText || hasMedia || hasChildrenMedia;
};

const fetchThreadInsightsDirect = async (llt, threadId) => {
  try {
    // graph.threads.net 은 브라우저 CORS 미지원 → threadsApiProxy CF 경유
    const res = await graphGet(`${threadId}/insights`, {
      params: {
        metric: 'views, likes,replies,reposts',
        access_token: llt,
      },
    }, { retryOn5xx: false });
    const map = (res.data?.data || []).reduce((acc, item) => {
      acc[item.name] = item.values?.[0]?.value || 0;
      return acc;
    }, {});
    return map;
  } catch (_) {
    return { views: 0, likes: 0, replies: 0, reposts: 0 };
  }
};

const fetchThreadsDirect = async (llt, sinceUnix, untilUnix) => {
  let nextUrl = null;
  const all = [];
  do {
    const params = {
      fields: 'id,text,timestamp,media_product_type,username,media_type,media_url,permalink,children{media_type,media_url,alt_text},alt_text',
      since: sinceUnix,
      until: untilUnix,
      access_token: llt,
      limit: 300,
    };
    // graph.threads.net 은 브라우저 CORS 미지원 → threadsApiProxy CF 경유
    const res = nextUrl ? await graphGet(nextUrl) : await graphGet('me/threads', { params });
    const data = res.data?.data || [];
    // attach insights (skip repost)
    const withInsights = await Promise.all(
      data.map(async (t) => {
        if (t.media_type === 'REPOST_FACADE') {
          return { ...t, views: 0, likes: 0, replies: 0, reposts: 0 };
        }
        const insights = await fetchThreadInsightsDirect(llt, t.id);
        return { ...t, ...insights };
      })
    );
    const filtered = withInsights.filter(shouldKeepThread);
    all.push(...filtered);
    nextUrl = res.data?.paging?.next || null;
  } while (nextUrl);
  return all;
};

export const adminIngestByDateRange = async (userID, longLivedToken, sinceISO, untilISO) => {
  try {
    errorLogger.startTrace({ flow: 'admin_manual_ingest_range', userID, sinceISO, untilISO });
    const since = isoToUnixClamped(sinceISO, true);
    const until = Math.min(isoToUnixClamped(untilISO, false), nowUnix());
    const fetched = await fetchThreadsDirect(longLivedToken, since, until);
    const existing = await getExistingThreadsFromFirestore(userID);
    const unique = filterUniqueThreads(existing, fetched);
    if (unique.length > 0) {
      await updateThreadsToFirestore(userID, unique);
    }
    errorLogger.trace('range_ingest_done', { fetched: fetched.length, saved: unique.length });
    return { fetched: fetched.length, saved: unique.length };
  } catch (e) {
    await errorLogger.flushOnError({
      component: 'adminManualIngest',
      event: 'range_ingest_failed',
      userID,
      since: sinceISO,
      until: untilISO,
      message: e?.message,
      stack: e?.stack,
    });
    throw e;
  }
};

export const adminIngestLifetimeAuto = async (userID, longLivedToken) => {
  try {
    errorLogger.startTrace({ flow: 'admin_manual_ingest_auto', userID });
    const userRef = dbService.collection('user').doc(userID);
    const userDoc = await userRef.get();
    const since = userDoc.exists && userDoc.data()?.lastUpdated ? userDoc.data().lastUpdated : MIN_SINCE;
    const until = nowUnix();
    const fetched = await fetchThreadsDirect(longLivedToken, since, until);
    const existing = await getExistingThreadsFromFirestore(userID);
    const unique = filterUniqueThreads(existing, fetched);
    if (unique.length > 0) {
      await updateThreadsToFirestore(userID, unique);
    }
    errorLogger.trace('auto_ingest_done', { fetched: fetched.length, saved: unique.length });
    return { savedCount: unique.length };
  } catch (e) {
    await errorLogger.flushOnError({
      component: 'adminManualIngest',
      event: 'auto_ingest_failed',
      userID,
      message: e?.message,
      stack: e?.stack,
    });
    throw e;
  }
};

// 신규 가입 기준 전체 수집: 2023-07-05T07:00:00Z ~ 오늘(UTC 23:59:59)
export const adminIngestFullFromDefault = async (userID, longLivedToken) => {
  const DEFAULT_SINCE_ISO = '2023-07-05T07:00:00Z';
  const endOfToday = new Date();
  const todayISO = new Date(
    Date.UTC(endOfToday.getUTCFullYear(), endOfToday.getUTCMonth(), endOfToday.getUTCDate(), 23, 59, 59)
  ).toISOString();
  return adminIngestByDateRange(userID, longLivedToken, DEFAULT_SINCE_ISO, todayISO);
};


/**
 * 관리자용: 유저 threads 데이터 전체 리셋 후, LLT로 전체 재수집
 * - 모든 user/{id}/threads/* 문서 삭제(배치)
 * - threadsMetadata/lastUpdated 초기화
 * - adminIngestFullFromDefault로 전체 재수집 및 저장
 */
export const adminResetAndReingestFull = async (userID, longLivedToken) => {
  try {
    errorLogger.startTrace({ flow: 'admin_reset_reingest_full', userID });

    // 1) threads 서브컬렉션 전체 삭제 (배치 처리)
    const userRef = dbService.collection('user').doc(userID);
    const threadsRef = userRef.collection('threads');
    const snapshot = await threadsRef.get();
    let deleted = 0;
    const BATCH_SIZE = 300;
    for (let i = 0; i < snapshot.docs.length; i += BATCH_SIZE) {
      const batch = dbService.batch();
      const part = snapshot.docs.slice(i, i + BATCH_SIZE);
      part.forEach(doc => batch.delete(doc.ref));
      await batch.commit();
      deleted += part.length;
    }

    // 2) 메타데이터 및 lastUpdated 초기화
    await userRef.set({
      lastUpdated: null,
      threadsMetadata: {
        nextTargetDocument: 'threads',
        isApproachingLimit: false,
        currentDocumentSize: 0,
        lastUpdatedAt: new Date().toISOString(),
      }
    }, { merge: true });

    // 보조: 초기 문서 존재 시 메타 재계산 시도(없으면 내부에서 안전 실패)
    try { await initializeShardingMetadata(userID); } catch (_) {}

    // 3) 전체 재수집 (신규 가입 기준 기간 전체)
    const res = await adminIngestFullFromDefault(userID, longLivedToken);

    errorLogger.trace('admin_reset_reingest_done', { deletedDocs: deleted, fetched: res.fetched, saved: res.saved });
    return { deletedDocs: deleted, fetched: res.fetched, saved: res.saved };
  } catch (e) {
    await errorLogger.flushOnError({
      component: 'adminManualIngest',
      event: 'reset_reingest_failed',
      userID,
      message: e?.message,
      stack: e?.stack,
    });
    throw e;
  }
};


